/*
 * Creative Studios runner — an original endless-runner minigame.
 * The player hops a pixel character over stacked text obstacles naming
 * the AI-content pitfalls the studio avoids.
 */
class CreativeStudiosGame extends HTMLElement {
  connectedCallback() {
    this.canvas = this.querySelector('[data-game-canvas]');
    this.prompt = this.querySelector('[data-game-prompt]');
    if (!this.canvas) return;

    this.ctx = this.canvas.getContext('2d');

    try {
      this.labels = JSON.parse(this.dataset.obstacles || '[]');
    } catch (e) {
      this.labels = [];
    }
    if (!this.labels.length) this.labels = ['ai slop'];

    this.state = 'idle';
    this.raf = null;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);

    this.FONT = "'Courier Prime', 'Courier New', monospace";
    this.INK = '#0a0a0a';
    this.MUTED = '#a0a0a0';
    this.FAINT = '#d6d6d6';

    this.GRAVITY = 0.58;
    this.JUMP_V = -11.5;
    this.START_SPEED = 3.3;
    this.MAX_SPEED = 7.6;

    this.highScore = this.readHighScore();

    this.onResize = this.resize.bind(this);
    this.onKeyDown = this.handleKeyDown.bind(this);
    this.onPointerDown = this.handlePointerDown.bind(this);

    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    document.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('resize', this.onResize);

    // Stop burning frames when the game scrolls out of sight.
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting && this.state === 'running') this.pause();
      });
    });
    this.observer.observe(this);

    this.resize();
    this.reset();
    this.drawFrame();
  }

  disconnectedCallback() {
    this.stopLoop();
    if (this.observer) this.observer.disconnect();
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    document.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('resize', this.onResize);
  }

  readHighScore() {
    try {
      return parseInt(window.localStorage.getItem('cs-runner-high') || '0', 10) || 0;
    } catch (e) {
      return 0;
    }
  }

  writeHighScore(value) {
    try {
      window.localStorage.setItem('cs-runner-high', String(value));
    } catch (e) {
      /* private mode — score just won't persist */
    }
  }

  resize() {
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    if (!width || !height) return;

    this.w = width;
    this.h = height;
    this.canvas.width = Math.round(width * this.dpr);
    this.canvas.height = Math.round(height * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    this.groundY = this.h - 26;
    if (this.runner) this.clampRunner();
    this.drawFrame();
  }

  clampRunner() {
    const floor = this.groundY - this.runner.h;
    if (this.runner.y > floor) {
      this.runner.y = floor;
      this.runner.vy = 0;
      this.runner.grounded = true;
    }
  }

  reset() {
    this.speed = this.START_SPEED;
    this.distance = 0;
    this.score = 0;
    this.obstacles = [];
    this.spawnIn = 60;
    this.legTimer = 0;
    this.legFrame = 0;

    this.runner = { x: 34, y: 0, w: 20, h: 26, vy: 0, grounded: true };
    this.runner.y = this.groundY - this.runner.h;

    this.clouds = [
      { x: this.w * 0.45, y: 26, s: 0.28 },
      { x: this.w * 0.85, y: 44, s: 0.18 },
    ];
    this.speckles = [];
    for (let i = 0; i < 22; i += 1) {
      this.speckles.push({ x: Math.random() * this.w, len: 1 + Math.random() * 5 });
    }
  }

  handleKeyDown(event) {
    const isJumpKey = event.code === 'Space' || event.code === 'ArrowUp';
    if (!isJumpKey) return;

    // Only claim the spacebar while actually playing or focused, so the key
    // still scrolls the page everywhere else.
    const focused = this.contains(document.activeElement);
    if (this.state !== 'running' && !focused) return;

    event.preventDefault();
    this.activate();
  }

  handlePointerDown(event) {
    event.preventDefault();
    this.canvas.focus();
    this.activate();
  }

  activate() {
    if (this.state === 'idle' || this.state === 'paused') {
      this.start();
    } else if (this.state === 'running') {
      this.jump();
    } else if (this.state === 'over') {
      this.reset();
      this.start();
    }
  }

  start() {
    this.state = 'running';
    if (this.prompt) this.prompt.hidden = true;
    this.lastTime = performance.now();
    this.startLoop();
  }

  pause() {
    this.state = 'paused';
    this.stopLoop();
  }

  jump() {
    if (!this.runner.grounded) return;
    this.runner.vy = this.JUMP_V;
    this.runner.grounded = false;
  }

  startLoop() {
    this.stopLoop();
    const step = (now) => {
      const delta = Math.min((now - this.lastTime) / (1000 / 60), 3);
      this.lastTime = now;
      this.update(delta);
      this.drawFrame();
      if (this.state === 'running') this.raf = window.requestAnimationFrame(step);
    };
    this.raf = window.requestAnimationFrame(step);
  }

  stopLoop() {
    if (this.raf) window.cancelAnimationFrame(this.raf);
    this.raf = null;
  }

  measureObstacle(label) {
    const lines = this.wrap(label, 10);
    const charW = 5.5;
    const lineH = 11;
    const pad = 6;
    const longest = lines.reduce((max, line) => Math.max(max, line.length), 0);
    return {
      lines,
      w: Math.round(longest * charW + pad * 2),
      h: Math.round(lines.length * lineH + pad * 2),
      lineH,
      pad,
    };
  }

  wrap(text, maxChars) {
    const words = String(text).split(/\s+/);
    const lines = [];
    let line = '';
    words.forEach((word) => {
      const candidate = line ? line + ' ' + word : word;
      if (candidate.length > maxChars && line) {
        lines.push(line);
        line = word;
      } else {
        line = candidate;
      }
    });
    if (line) lines.push(line);
    return lines.slice(0, 3);
  }

  spawn() {
    const label = this.labels[Math.floor(Math.random() * this.labels.length)];
    const box = this.measureObstacle(label);
    this.obstacles.push({
      x: this.w + 10,
      w: box.w,
      h: box.h,
      lines: box.lines,
      lineH: box.lineH,
      pad: box.pad,
      counted: false,
    });

    // Keep the next gap comfortably clearable at the current speed.
    const airborneFrames = (2 * Math.abs(this.JUMP_V)) / this.GRAVITY;
    const minGap = this.speed * airborneFrames * 0.85 + box.w;
    this.spawnIn = Math.round(minGap / this.speed + Math.random() * 45);
  }

  update(delta) {
    this.distance += this.speed * delta;
    this.score = Math.floor(this.distance / 12);
    if (this.speed < this.MAX_SPEED) this.speed += 0.0013 * delta;

    // runner physics
    this.runner.vy += this.GRAVITY * delta;
    this.runner.y += this.runner.vy * delta;
    const floor = this.groundY - this.runner.h;
    if (this.runner.y >= floor) {
      this.runner.y = floor;
      this.runner.vy = 0;
      this.runner.grounded = true;
    }

    this.legTimer += delta;
    if (this.legTimer > 5) {
      this.legTimer = 0;
      this.legFrame = this.legFrame === 0 ? 1 : 0;
    }

    // scenery
    this.clouds.forEach((cloud) => {
      cloud.x -= cloud.s * delta;
      if (cloud.x < -40) {
        cloud.x = this.w + Math.random() * 60;
        cloud.y = 20 + Math.random() * 34;
      }
    });
    this.speckles.forEach((speck) => {
      speck.x -= this.speed * delta;
      if (speck.x < -6) {
        speck.x = this.w + Math.random() * 30;
        speck.len = 1 + Math.random() * 5;
      }
    });

    // obstacles
    this.spawnIn -= delta;
    if (this.spawnIn <= 0) this.spawn();

    this.obstacles.forEach((obstacle) => {
      obstacle.x -= this.speed * delta;
    });
    this.obstacles = this.obstacles.filter((obstacle) => obstacle.x + obstacle.w > -10);

    const r = this.runner;
    this.obstacles.forEach((obstacle) => {
      const oy = this.groundY - obstacle.h;
      const hit =
        r.x + r.w - 3 > obstacle.x &&
        r.x + 3 < obstacle.x + obstacle.w &&
        r.y + r.h - 2 > oy;
      if (hit) this.gameOver();
    });
  }

  gameOver() {
    this.state = 'over';
    this.stopLoop();
    if (this.score > this.highScore) {
      this.highScore = this.score;
      this.writeHighScore(this.score);
    }
    if (this.prompt) {
      this.prompt.hidden = false;
      this.prompt.textContent = this.promptText('over');
    }
    this.drawFrame();
  }

  promptText(state) {
    const tap = window.matchMedia('(hover: none)').matches;
    if (state === 'over') return tap ? 'Tap to play again' : 'Press space to play again';
    return tap ? 'Tap to play' : 'Click or press space to play';
  }

  pad5(value) {
    return String(Math.min(value, 99999)).padStart(5, '0');
  }

  drawFrame() {
    // resize() can fire before reset() has built the world, so bail until ready
    if (!this.ctx || !this.w || !this.runner) return;
    const ctx = this.ctx;

    ctx.clearRect(0, 0, this.w, this.h);

    // clouds
    ctx.fillStyle = this.FAINT;
    this.clouds.forEach((cloud) => {
      ctx.fillRect(cloud.x, cloud.y, 14, 3);
      ctx.fillRect(cloud.x + 4, cloud.y - 3, 8, 3);
    });

    // ground
    ctx.fillStyle = this.MUTED;
    ctx.fillRect(0, this.groundY, this.w, 1);
    ctx.fillStyle = this.FAINT;
    this.speckles.forEach((speck) => {
      ctx.fillRect(speck.x, this.groundY + 4, speck.len, 1);
    });

    this.drawRunner(ctx);

    this.obstacles.forEach((obstacle) => this.drawObstacle(ctx, obstacle));

    // score
    ctx.font = `10px ${this.FONT}`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    ctx.fillStyle = this.MUTED;
    const hi = this.highScore > 0 ? `HI ${this.pad5(this.highScore)}  ` : '';
    ctx.fillText(`${hi}${this.pad5(this.score)}`, this.w - 8, 8);
    ctx.textAlign = 'left';
  }

  drawRunner(ctx) {
    const r = this.runner;
    const x = Math.round(r.x);
    const y = Math.round(r.y);
    ctx.fillStyle = this.INK;

    // head
    ctx.fillRect(x + 6, y, 12, 9);
    // eye (knocked out of the head block)
    ctx.clearRect(x + 14, y + 3, 2, 2);
    // neck + body
    ctx.fillRect(x + 4, y + 9, 12, 10);
    // arm
    ctx.fillRect(x + 15, y + 11, 5, 2);

    // legs — alternate while grounded, tucked mid-jump
    if (!r.grounded) {
      ctx.fillRect(x + 5, y + 19, 4, 5);
      ctx.fillRect(x + 11, y + 19, 4, 4);
    } else if (this.legFrame === 0) {
      ctx.fillRect(x + 5, y + 19, 4, 7);
      ctx.fillRect(x + 12, y + 19, 4, 5);
    } else {
      ctx.fillRect(x + 5, y + 19, 4, 5);
      ctx.fillRect(x + 12, y + 19, 4, 7);
    }
  }

  drawObstacle(ctx, obstacle) {
    const x = Math.round(obstacle.x);
    const y = Math.round(this.groundY - obstacle.h);

    ctx.strokeStyle = this.INK;
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 0.5, y + 0.5, obstacle.w - 1, obstacle.h - 1);

    ctx.font = `9px ${this.FONT}`;
    ctx.fillStyle = this.INK;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    obstacle.lines.forEach((line, index) => {
      ctx.fillText(line, x + obstacle.w / 2, y + obstacle.pad + index * obstacle.lineH);
    });
    ctx.textAlign = 'left';
  }
}

customElements.define('creative-studios-game', CreativeStudiosGame);
